import { useCallback } from 'react';
import { IDENTITY_PROVIDERS } from '@24i/nxg-core-utils/src/constants';
import { useFeature } from '@24i/nxg-sdk-smartott-shared/src/context/AppSettingsData';
import { useAdobePrimetime } from '@24i/adobe-primetime/src';
import { useRouter } from '@24i/nxg-core-router/src/NextRouter';
import { SOTT_DEFAULT_WEB_SCREENS } from '../../navigation/constants';

export const useLogoutNavigation = () => {
    const router = useRouter();
    const landingScreenEnabled = useFeature('landingScreen')?.enabled;
    const {
        authState: { authenticated: adobeAuthenticated },
    } = useAdobePrimetime();

    const navigateAfterFlowIdLogout = useCallback(async (): Promise<void> => {
        const homeLink = SOTT_DEFAULT_WEB_SCREENS.Home.getLink();
        await router.push(homeLink.href, homeLink.as);
        router.reload(); // we need to this to refresh AppState, more info [PRDSAPPSRN-12561];
    }, [router]);

    const resetNavigation = useCallback(async (): Promise<void> => {
        let source = SOTT_DEFAULT_WEB_SCREENS.Home.getLink();
        if (landingScreenEnabled) {
            source = SOTT_DEFAULT_WEB_SCREENS.Landing.getLink();
        }

        await router.push(source.href, source.as);
    }, [landingScreenEnabled]);

    const navigateAfterAdobeLogout = useCallback(() => {
        if (!landingScreenEnabled) {
            const homeLink = SOTT_DEFAULT_WEB_SCREENS.Home.getLink();
            router.push(
                {
                    pathname: homeLink.href,
                    query: { shouldSignOut: true },
                },
                homeLink.as
            );
            return;
        }
        const mvpdLink = SOTT_DEFAULT_WEB_SCREENS.MvpdSignIn.getLink();
        router.push(
            {
                pathname: mvpdLink.href,
                query: { didInitialise: true, shouldSignOut: true },
            },
            mvpdLink.as
        );
    }, [router, landingScreenEnabled]);

    const navigateAfterLogout = useCallback(
        async (provider?: IDENTITY_PROVIDERS): Promise<unknown> => {
            if (adobeAuthenticated) return navigateAfterAdobeLogout();

            switch (provider) {
                case IDENTITY_PROVIDERS.FLOWID:
                case IDENTITY_PROVIDERS.FLOWID_V2:
                    return navigateAfterFlowIdLogout();
                default:
                    return resetNavigation();
            }
        },
        [navigateAfterAdobeLogout, navigateAfterFlowIdLogout, resetNavigation]
    );

    return { navigateAfterLogout };
};
